import LinkIcon from '@mui/icons-material/Link';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import TuneIcon from '@mui/icons-material/Tune';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import { Box, Tooltip, Typography, useTheme } from '@mui/material';
import { amber, blue, deepOrange, green, indigo } from '@mui/material/colors';
import { format } from 'date-fns';
import { toZonedTime } from 'date-fns-tz';
import noop from 'lodash/noop';
import React, { forwardRef, useCallback, useMemo, useState } from 'react';
import Highlighter from 'react-highlight-words';
import { useDispatch, useSelector } from 'react-redux';

import CancelSendIcon from '../../../assets/images/icon-cancel-send.svg';
import DisableEmailNotificationsIcon from '../../../assets/images/icon-envelope.svg';
import GlobeBookIcon from '../../../assets/images/icon-globe-book.svg';
import IconLocation from '../../../assets/images/icon-location.svg';
import LockPersonOutlinedIcon from '../../../assets/images/icon-lock-person.svg';
import PasswordProtectedIcon from '../../../assets/images/icon-password-protected.svg';

import { DateFormatsEnum } from '../../../constants/date';
import { COLUMN_IDS, VISIBLE_ONE_LINKS_COLS_INITIAL_STATE } from '../../../constants/oneLink';
import { INITIAL_PAGE } from '../../../constants/table';
import useClearFiltersCallback from '../../../hooks/useClearFiltersCallback';
import useColumnsVisibility from '../../../hooks/useColumnsVisibility';
import usePopover from '../../../hooks/usePopover';
import { changeOneLinksColumnsVisibility } from '../../../store/actions/tools';
import {
  selectCurrentOrganizationDepartmentList,
  selectIsProductMarketplaceEnabled,
} from '../../../store/selectors/organizations';
import { selectBusinessReasons } from '../../../store/selectors/reasons';
import { selectOneLinksVisibleColumns } from '../../../store/selectors/tools';
import { selectUsers } from '../../../store/selectors/users';
import { IDateInfo, IOneLink, IOneLinkManagerFilters } from '../../../types/oneLink';
import { IFiltersRef } from '../../../types/tables';
import { IUser } from '../../../types/users';
import { detectUserTimezone, isValidDate } from '../../../utils/date';
import { getFullName } from '../../../utils/helpers';
import { getHighlightStyles } from '../../../utils/highlightStyles';
import { getWarehouseItems } from '../../../utils/marketplaceSend';
import { calculateDaysLeft, getReportGiftBudget, getReportItemSources } from '../../../utils/reports';
import { defaultTableStyles, getDefaultStickyStyles } from '../../../utils/tables';
import { IPaginatedListChangeEvent } from '../../PaginatedList/PaginatedList';
import ReportCampaignName from '../../ReportsComponent/components/ReportCampaignName';
import ReportGiftBudget from '../../ReportsComponent/components/ReportGiftBudget';
import ReportItemSource from '../../ReportsComponent/components/ReportItemSource';
import ColumnsVisibilityPopover from '../../UsersComponent/components/ColumnsVisibilityPopover';
import {
  CopyableButton,
  CopyableLink,
  PageContent,
  SettingsItem,
  TableFilteredEmptyPlaceholder,
  TableMUI,
} from '../../_mui';
import { OneLinkContextMenu } from '../components/OneLinksContextMenu';
import OneLinkStatus from '../components/OneLinkStatus';
import { useOneLinksContextMenu } from '../hooks/useOneLinksContextMenu';

const ITEM_SOURCE_COL_WIDTH = 240;
const GIFT_BUDGET_COL_WIDTH = 180;

const MARKETPLACE_COLUMN_IDS: string[] = [COLUMN_IDS.ITEM_SOURCE, COLUMN_IDS.GIFT_BUDGET];

interface IProps {
  items: IOneLink[];
  totalCount: number;
  isOneLinksFetching: boolean;
  filters: IOneLinkManagerFilters;
  onChange: (paginationEvent: IPaginatedListChangeEvent) => void;
  onOpenDeactivateDialog: (item: IOneLink) => void;
  page: number;
  pageSize: number;
}

const OneLinkManagerListView = forwardRef<IFiltersRef, IProps>(
  (
    { items, totalCount, isOneLinksFetching, filters, onChange, onOpenDeactivateDialog, page, pageSize }: IProps,
    filtersRef,
  ) => {
    const theme = useTheme();
    const userTimezone = detectUserTimezone();
    const dispatch = useDispatch();

    const reasons = useSelector(selectBusinessReasons);
    const initialVisibleColumns = useSelector(selectOneLinksVisibleColumns);
    const allDepartments = useSelector(selectCurrentOrganizationDepartmentList);
    const isMarketplaceEnabled = useSelector(selectIsProductMarketplaceEnabled);

    const [menuPosition, setMenuPosition] = useState<{ top: number; left: number } | null>(null);
    const users = useSelector(selectUsers);

    const { query: searchQuery } = filters;

    const settingsIconStyle = useMemo(
      () => ({
        width: 16,
        height: 16,
      }),
      [],
    );

    const {
      visibleColumns,
      columnSearchQuery,
      setColumnSearchQuery,
      handleColumnVisibilityChange,
      handleToggleAllColumns,
      handleResetColumns,
      areAllColumnsVisible,
    } = useColumnsVisibility({
      initialState: initialVisibleColumns,
      requiredColumnIds: [COLUMN_IDS.TITLE, COLUMN_IDS.ACTIONS],
      onChange: (newState: Record<string, boolean>) => dispatch(changeOneLinksColumnsVisibility(newState)),
      defaultColumns: VISIBLE_ONE_LINKS_COLS_INITIAL_STATE,
    });

    const {
      anchorEl: visibleColsAnchorEl,
      position: popoverPosition,
      isOpen: isVisibleColsPopoverOpen,
      handleOpen: handleVisibleColsMenuOpen,
      handleClose: handleVisibleColsMenuClose,
    } = usePopover();

    const { menuAnchorEl, handleMenuClose, handleMenuOpen, selectedItem, handleRowClick } = useOneLinksContextMenu();

    const defaultColumnEvents = useMemo(
      () => ({
        onBodyCellDoubleClick: handleRowClick,
        onCellContextMenu: (e: React.MouseEvent<HTMLElement, MouseEvent>, oneLink: IOneLink, columnId?: string) => {
          if (columnId !== COLUMN_IDS.ACTIONS) {
            e.preventDefault();
            setMenuPosition({
              top: e.pageY,
              left: e.pageX,
            });
            handleMenuOpen(e, oneLink);
          }
        },
      }),
      [handleMenuOpen, handleRowClick],
    );

    const findFullNameByEmail = useCallback((userList: IUser[] | null | undefined, email?: string) => {
      const user = userList?.find((u) => u.email === email);
      return user ? getFullName(user.first_name, user.last_name) : email;
    }, []);

    const getAddressTooltipTitle = useCallback((isFixed: boolean, orgAddressIds?: string[]): string => {
      switch (true) {
        case !isFixed:
          return 'Recipient decides';
        case isFixed && orgAddressIds && orgAddressIds.length > 0:
          return 'Recipient decides from list only';
        default:
          return 'Fixed address';
      }
    }, []);

    const renderSettings = useCallback(
      (oneLink: IOneLink) => {
        const settings = [
          {
            condition: oneLink.enabled_2fa,
            backgroundColor: blue[50],
            tooltipTitle: `Two-Factor Authentication is enabled for ${oneLink.recipients_count} recipient${
              oneLink.recipients_count !== 1 ? 's' : ''
            }`,
            icon: <img src={LockPersonOutlinedIcon} alt="password protected" style={{ width: 18, height: 18 }} />,
          },
          {
            condition: !!oneLink.password,
            backgroundColor: theme.sass.settingsPasswordBgColor,
            tooltipTitle: 'Password protected',
            icon: <img src={PasswordProtectedIcon} alt="password protected" style={settingsIconStyle} />,
          },
          {
            condition: oneLink.allowed_domains !== null,
            backgroundColor: green[50],
            tooltipTitle: 'Domain limiting',
            icon: <img src={GlobeBookIcon} alt="domain limiting" style={settingsIconStyle} />,
          },
          {
            condition: true,
            backgroundColor: amber[50],
            tooltipTitle: `Address: ${getAddressTooltipTitle(
              oneLink.is_receiver_address_fixed,
              oneLink.org_address_ids,
            )}`,
            icon: <img src={IconLocation} alt="type of address settings" style={settingsIconStyle} />,
          },
          {
            condition: oneLink.disable_sender_email_notifications,
            backgroundColor: deepOrange[50],
            tooltipTitle: 'Disable sender email notifications',
            icon: <img src={CancelSendIcon} alt="cancel send" style={settingsIconStyle} />,
          },
          {
            condition: oneLink.disable_recipient_shipped_email,
            backgroundColor: indigo[50],
            tooltipTitle: 'Disable receiver email notifications',
            icon: (
              <img
                src={DisableEmailNotificationsIcon}
                alt="Disable Email Notifications Icon"
                style={{ width: 18, height: 18, position: 'relative', top: 1 }}
              />
            ),
          },
        ];
        return (
          <Box sx={{ display: 'flex', flexWrap: 'nowrap', gap: 1 }}>
            {settings.map((setting, index) => (
              <SettingsItem key={index} {...setting} />
            ))}
          </Box>
        );
      },
      [theme.sass, settingsIconStyle, getAddressTooltipTitle],
    );

    const getDateInfo = useCallback(
      (date: string | null | undefined, isExpiration = false): IDateInfo => {
        const isValidData = !!(date && isValidDate(new Date(date)));
        let zonedDate;
        let daysLeft;

        if (isValidData) {
          const zonedDateTime = toZonedTime(date, userTimezone.utc[0]);
          zonedDate = format(zonedDateTime, DateFormatsEnum.FullHumanReadableWithoutUTC);

          if (isExpiration) {
            daysLeft = calculateDaysLeft(date);
          }
        }
        return {
          isValid: isValidData,
          ...(isExpiration && { daysLeft }),
          timezone: userTimezone,
          zonedDate: `${zonedDate} (${userTimezone.abbr})`,
        };
      },
      [userTimezone],
    );

    const renderActionControls = useCallback(
      (oneLink: IOneLink) => {
        return (
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'baseline' }}>
            <Box
              onClick={(e) => {
                e.stopPropagation();
                handleRowClick(oneLink);
              }}
            >
              <Tooltip title="View summary" arrow placement="top">
                <VisibilityOutlinedIcon fontSize="small" color="action" />
              </Tooltip>
            </Box>
            <Box
              onClick={(e) => {
                e.stopPropagation();
                handleMenuOpen(e, oneLink);
              }}
            >
              <Tooltip title="More actions" arrow placement="top">
                <MoreVertIcon fontSize="small" color="action" />
              </Tooltip>
            </Box>
          </Box>
        );
      },
      [handleRowClick, handleMenuOpen],
    );

    const allColumns = useMemo(() => {
      const { stickyLeftCellStyle, actionsCellStyle } = getDefaultStickyStyles(theme, 85);
      const { bodyCellStyle, ellipsisContainer, minWidthBodyCellStyle, doubleLineEllipsisContainer } =
        defaultTableStyles(theme);
      const { mark, accentLink } = getHighlightStyles(theme);

      return [
        {
          id: COLUMN_IDS.TITLE,
          label: 'Title',
          ...defaultColumnEvents,
          headerCellStyle: {
            ...stickyLeftCellStyle,
            zIndex: { xs: '2 !important', sm: '11 !important' },
            paddingLeft: 2.5,
          },
          bodyCellStyle: {
            ...bodyCellStyle,
            ...stickyLeftCellStyle,
            minWidth: 200,
          },
          render: (oneLink: IOneLink) => {
            return (
              <Box>
                {oneLink?.title ? (
                  <CopyableLink
                    value={oneLink?.title}
                    onTextClick={() => handleRowClick(oneLink)}
                    textTooltip={oneLink.title}
                    copyTooltip="Copy title"
                    toastLabel="Title"
                    additionalActions={
                      oneLink.url
                        ? [
                            {
                              icon: <LinkIcon />,
                              value: oneLink.url,
                              tooltip: 'Copy URL',
                              toastLabel: 'URL',
                            },
                          ]
                        : []
                    }
                  >
                    <Highlighter
                      style={{ ...ellipsisContainer, color: 'inherit' }}
                      highlightStyle={mark}
                      searchWords={[searchQuery]}
                      autoEscape
                      textToHighlight={oneLink?.title || 'No title'}
                    />
                  </CopyableLink>
                ) : (
                  <Typography
                    onClick={() => handleRowClick(oneLink)}
                    sx={{ '&:hover': { textDecoration: 'underline', color: theme?.wl?.portal_accent_color } }}
                  >
                    No title
                  </Typography>
                )}
              </Box>
            );
          },
        },
        {
          id: COLUMN_IDS.STATUS,
          label: 'Status',
          ...defaultColumnEvents,
          bodyCellStyle: minWidthBodyCellStyle,
          render: (oneLink: IOneLink) => <OneLinkStatus status={oneLink.status} />,
        },
        {
          id: COLUMN_IDS.CAMPAIGN_NAME,
          label: 'Campaign name',
          ...defaultColumnEvents,
          bodyCellStyle: minWidthBodyCellStyle,
          render: (oneLink: IOneLink) => (
            <ReportCampaignName
              preCreatedEngagementId={oneLink.pre_created_engagement_id}
              campaignName={oneLink.campaign_name}
              searchQuery={searchQuery}
              isPYGChipShown={!!oneLink.pick_campaign_ids}
              highlightStyle={mark}
              textSx={ellipsisContainer}
            />
          ),
        },
        {
          id: COLUMN_IDS.ITEM,
          label: 'Inventory',
          ...defaultColumnEvents,
          bodyCellStyle: minWidthBodyCellStyle,
          render: (oneLink: IOneLink) => {
            const itemNames = [
              ...getWarehouseItems(oneLink.items).map((item) => item.name),
              ...Object.values(oneLink.marketplace ?? {}).map((item) => item.name),
            ]
              .filter((name) => !!name)
              .join(', ');
            return (
              <Typography sx={{ ...ellipsisContainer }} title={itemNames}>
                <Highlighter highlightStyle={mark} searchWords={[searchQuery]} autoEscape textToHighlight={itemNames} />
              </Typography>
            );
          },
        },
        {
          id: COLUMN_IDS.ITEM_SOURCE,
          label: 'Item source',
          ...defaultColumnEvents,
          headerCellStyle: { minWidth: ITEM_SOURCE_COL_WIDTH, maxWidth: ITEM_SOURCE_COL_WIDTH },
          bodyCellStyle: { ...bodyCellStyle, minWidth: ITEM_SOURCE_COL_WIDTH, maxWidth: ITEM_SOURCE_COL_WIDTH },
          render: (oneLink: IOneLink) => <ReportItemSource sources={getReportItemSources(oneLink)} />,
        },
        {
          id: COLUMN_IDS.GIFT_BUDGET,
          label: 'Gift budget',
          ...defaultColumnEvents,
          headerCellStyle: { minWidth: GIFT_BUDGET_COL_WIDTH, maxWidth: GIFT_BUDGET_COL_WIDTH },
          bodyCellStyle: { ...bodyCellStyle, minWidth: GIFT_BUDGET_COL_WIDTH, maxWidth: GIFT_BUDGET_COL_WIDTH },
          render: (oneLink: IOneLink) => <ReportGiftBudget budget={getReportGiftBudget(oneLink)} />,
        },
        {
          id: COLUMN_IDS.DEPARTMENT,
          label: 'Department',
          ...defaultColumnEvents,
          bodyCellStyle: minWidthBodyCellStyle,
          render: (oneLink: IOneLink) => {
            const department = allDepartments?.find((dept) => dept.uid === oneLink.department_id);
            const departmentName = department?.name || '';
            return (
              <Typography sx={{ ...ellipsisContainer }} title={departmentName}>
                {departmentName}
              </Typography>
            );
          },
        },
        {
          id: COLUMN_IDS.ONE_LINK_URL,
          label: 'One link URL',
          ...defaultColumnEvents,
          bodyCellStyle: minWidthBodyCellStyle,
          render: (oneLink: IOneLink) => {
            return oneLink.url ? (
              <CopyableButton
                value={oneLink.url}
                style={{ color: theme.palette.primary.main }}
                tooltipText="Copy URL"
                toastLabel="Claim URL"
              >
                <span style={accentLink}>Claim link</span>
              </CopyableButton>
            ) : null;
          },
        },
        {
          id: COLUMN_IDS.CREATED_BY,
          label: 'Created by',
          ...defaultColumnEvents,
          bodyCellStyle,
          render: (oneLink: IOneLink) => {
            const { created_by } = oneLink;
            const fullName = findFullNameByEmail(users, created_by);

            return (
              <Box>
                <Tooltip title={fullName} arrow placement="top">
                  <Typography component="span" sx={{ ...ellipsisContainer }}>
                    {fullName ?? ''}
                  </Typography>
                </Tooltip>
                <Tooltip title={created_by} arrow placement="top">
                  <Typography
                    component="span"
                    sx={{
                      ...ellipsisContainer,
                      color: (th) => th.palette.text.secondary,
                    }}
                  >
                    {created_by}
                  </Typography>
                </Tooltip>
              </Box>
            );
          },
        },
        {
          id: COLUMN_IDS.SENDER,
          label: 'Sender',
          ...defaultColumnEvents,
          bodyCellStyle,
          render: (oneLink: IOneLink) => {
            const { sender_first_name, sender_last_name, sender_email } = oneLink;
            const fullName = getFullName(sender_first_name, sender_last_name);

            return (
              <Box>
                <Tooltip title={fullName} arrow placement="top">
                  <Typography component="span" sx={{ ...ellipsisContainer }}>
                    <Highlighter
                      highlightStyle={mark}
                      searchWords={[searchQuery]}
                      autoEscape
                      textToHighlight={fullName}
                    />
                  </Typography>
                </Tooltip>
                <Tooltip title={sender_email} arrow placement="top">
                  <Typography
                    component="span"
                    sx={{
                      ...ellipsisContainer,
                      color: (th) => th.palette.text.secondary,
                    }}
                  >
                    <Highlighter
                      highlightStyle={mark}
                      searchWords={[searchQuery]}
                      autoEscape
                      textToHighlight={sender_email}
                    />
                  </Typography>
                </Tooltip>
              </Box>
            );
          },
        },
        {
          id: COLUMN_IDS.CONFIRMED_SENDS,
          label: 'Confirmed sends',
          ...defaultColumnEvents,
          bodyCellStyle: minWidthBodyCellStyle,
          render: (oneLink: IOneLink) => {
            return <Typography>{oneLink?.use_times_count || 0}</Typography>;
          },
        },
        {
          id: COLUMN_IDS.REDEMPTION_LIMIT,
          label: 'Redemption limit',
          ...defaultColumnEvents,
          bodyCellStyle: minWidthBodyCellStyle,
          render: (oneLink: IOneLink) => {
            return <Typography>{oneLink?.max_submit_count || ''}</Typography>;
          },
        },
        {
          id: COLUMN_IDS.SETTINGS,
          label: 'Settings',
          ...defaultColumnEvents,
          bodyCellStyle: minWidthBodyCellStyle,
          render: renderSettings,
        },
        {
          id: COLUMN_IDS.CREATED_AT,
          label: 'Date created',
          ...defaultColumnEvents,
          bodyCellStyle: minWidthBodyCellStyle,
          render: (oneLink: IOneLink) => {
            const { zonedDate, isValid } = getDateInfo(oneLink.created_at);
            return isValid ? <Typography color="text.primary">{zonedDate}</Typography> : null;
          },
        },
        {
          id: COLUMN_IDS.EXPIRATION_DATE,
          label: 'Expiration date',
          ...defaultColumnEvents,
          bodyCellStyle: minWidthBodyCellStyle,
          render: (oneLink: IOneLink) => {
            const { zonedDate, isValid, daysLeft } = getDateInfo(oneLink.expiration_date, true);

            return (
              <>
                {isValid && (
                  <Tooltip title={daysLeft} arrow placement="top">
                    <Typography color="text.primary" sx={{ cursor: 'pointer' }}>
                      {zonedDate}
                    </Typography>
                  </Tooltip>
                )}
              </>
            );
          },
        },
        {
          id: COLUMN_IDS.BUSINESS_REASON,
          label: 'Business reason',
          ...defaultColumnEvents,
          bodyCellStyle: minWidthBodyCellStyle,
          render: (oneLink: IOneLink) => {
            const reason = reasons?.find((r) => r.uid === oneLink?.business_reason_id);
            return (
              <Tooltip title={reason?.name || oneLink?.business_reason_id || ''} arrow placement="top">
                <Typography component="span" sx={{ ...ellipsisContainer }}>
                  {reason?.name || ''}
                </Typography>
              </Tooltip>
            );
          },
        },
        {
          id: COLUMN_IDS.INTERNAL_NOTES,
          label: 'Internal notes',
          ...defaultColumnEvents,
          bodyCellStyle,
          render: (oneLink: IOneLink) => {
            return <Typography sx={{ ...doubleLineEllipsisContainer }}>{oneLink?.internal_notes || ''}</Typography>;
          },
        },
        {
          id: COLUMN_IDS.DISABLED_BY,
          label: 'Disabled by',
          ...defaultColumnEvents,
          bodyCellStyle: minWidthBodyCellStyle,
          render: (oneLink: IOneLink) => {
            const { disabled_by } = oneLink;
            const fullName = findFullNameByEmail(users, disabled_by);

            return (
              <Box>
                <Tooltip title={fullName} arrow placement="top">
                  <Typography component="span" sx={{ ...ellipsisContainer }}>
                    {fullName ?? ''}
                  </Typography>
                </Tooltip>
                <Tooltip title={disabled_by} arrow placement="top">
                  <Typography
                    component="span"
                    sx={{
                      ...ellipsisContainer,
                      color: (th) => th.palette.text.secondary,
                    }}
                  >
                    {disabled_by}
                  </Typography>
                </Tooltip>
              </Box>
            );
          },
        },
        {
          id: COLUMN_IDS.ON_HOLD_UNTIL,
          label: 'On hold until',
          ...defaultColumnEvents,
          bodyCellStyle: minWidthBodyCellStyle,
          render: (oneLink: IOneLink) => {
            const { zonedDate, isValid } = getDateInfo(oneLink.on_hold_until);
            return isValid ? <Typography color="text.primary">{zonedDate}</Typography> : null;
          },
        },
        {
          id: COLUMN_IDS.ACTIONS,
          label: (
            <Tooltip title="Show/hide columns" arrow placement="top">
              <TuneIcon color="action" />
            </Tooltip>
          ),
          headerCellStyle: actionsCellStyle,
          bodyCellStyle: actionsCellStyle,
          onHeaderCellClick: (e: React.MouseEvent<HTMLElement>) => {
            handleVisibleColsMenuOpen(e);
          },
          render: renderActionControls,
        },
      ].filter((column) => isMarketplaceEnabled || !MARKETPLACE_COLUMN_IDS.includes(column.id));
    }, [
      theme,
      defaultColumnEvents,
      searchQuery,
      handleVisibleColsMenuOpen,
      renderSettings,
      getDateInfo,
      reasons,
      users,
      findFullNameByEmail,
      renderActionControls,
      handleRowClick,
      allDepartments,
      isMarketplaceEnabled,
    ]);

    const handlePageChange = useCallback(
      (newPage: number) => {
        if (onChange) {
          onChange({
            page: newPage,
            pageSize,
            setPage: noop,
            setPageSize: noop,
          });
        }
      },
      [onChange, pageSize],
    );

    const handlePageSizeChange = useCallback(
      (newPageSize: number) => {
        if (onChange) {
          onChange({
            page: INITIAL_PAGE,
            pageSize: newPageSize,
            setPage: noop,
            setPageSize: noop,
          });
        }
      },
      [onChange],
    );

    const columns = useMemo(() => {
      return allColumns.filter((column) => visibleColumns[column.id]);
    }, [allColumns, visibleColumns]);

    const handleLocalMenuClose = useCallback(() => {
      setMenuPosition(null);
      handleMenuClose();
    }, [handleMenuClose]);

    const handleClearFilters = useClearFiltersCallback(
      filtersRef && typeof filtersRef !== 'function' ? filtersRef : null,
    );

    const placeholder = useMemo(
      () => <TableFilteredEmptyPlaceholder itemType="one links" onClearFilters={handleClearFilters} />,
      [handleClearFilters],
    );

    return (
      <PageContent>
        <TableMUI
          maxHeight="unset"
          containerStyle={{ width: '100%' }}
          data={items}
          columns={columns}
          page={page}
          rowsPerPage={pageSize}
          totalCount={totalCount}
          onPageChange={handlePageChange}
          onRowsPerPageChange={handlePageSizeChange}
          isLoading={isOneLinksFetching}
          emptyPlaceholderComponent={placeholder}
          activeRow={selectedItem}
          menuAnchorEl={menuAnchorEl}
          handleMenuClose={handleLocalMenuClose}
        />
        <OneLinkContextMenu
          menuPosition={menuPosition}
          anchorEl={menuAnchorEl}
          selectedItem={selectedItem}
          onClose={handleLocalMenuClose}
          onDeactivate={() => selectedItem && onOpenDeactivateDialog(selectedItem)}
        />
        <ColumnsVisibilityPopover
          anchorEl={visibleColsAnchorEl}
          position={popoverPosition}
          open={isVisibleColsPopoverOpen}
          onClose={handleVisibleColsMenuClose}
          columns={allColumns}
          visibleColumns={visibleColumns}
          searchQuery={columnSearchQuery}
          onSearchChange={setColumnSearchQuery}
          onColumnVisibilityChange={handleColumnVisibilityChange}
          onToggleAll={handleToggleAllColumns}
          onReset={handleResetColumns}
          requiredColumnIds={[COLUMN_IDS.TITLE, COLUMN_IDS.ACTIONS]}
          areAllColumnsVisible={areAllColumnsVisible}
        />
      </PageContent>
    );
  },
);

export default OneLinkManagerListView;
